import {FC, useState} from "react";
import {
  Oem,
  OemInput,
  oemUniqueKey
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {TextInput} from "@interstate/components/TextInput";
import {Button} from "@interstate/components/Button";
import {Tooltip} from "@interstate/components/Tooltip";
import {PlusIcon, TrashIcon, XMarkIcon} from "@interstate/components/Icons";
import {
  SimpleTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@interstate/components/SimpleTable";
import {Typography} from "@interstate/components/Typography";
import {TabRow} from "../../../../tab-content";
import {EditButton} from "../../../../action-buttons";
import {useOperationalEntity} from "../../../context";
import {useGraphqlLazyQuery, useGraphqlMutation} from "../../../../amplify/hooks";
import {
  lookupOperationalEntity as lookupOperationalEntityQuery,
  updateOperationalEntityOems as updateOemsMutation
} from "../../../../amplify/graphql";
import {
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  UpdateOperationalEntityOemsData,
  UpdateOperationalEntityOemsVariables,
  assertOperationalEntityPresent
} from "../../../backend";
import {useToastErrorHandler} from "../../../../backend";
import "./SyntheticOperationalOems.scss";

const emptyForm = {
  abbreviation: "",
  id: "",
  nameplateDescription: "",
  locationId: ""
};

export const SyntheticOperationalOems: FC<{
  id: string;
  oems: Oem[] | undefined;
}> = ({id, oems = []}) => {
  const {setEntity} = useOperationalEntity();
  const toastError = useToastErrorHandler();
  const [editing, setEditing] = useState(false);
  const [working, setWorking] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<{abbreviation?: string; nameplateDescription?: string}>({});

  const [lookupEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({amplifyQuery: lookupOperationalEntityQuery});

  const [updateOems] = useGraphqlMutation<
    UpdateOperationalEntityOemsData,
    UpdateOperationalEntityOemsVariables
  >({amplifyMutation: updateOemsMutation});

  const persistOems = async (next: OemInput[]) => {
    setWorking(true);
    try {
      const result = await updateOems({
        variables: {oemInput: {entityId: id, oems: next}}
      });
      if (result.data?.entity.success === false) {
        throw new Error("Failed to update OEMs");
      }
      const refreshed = await lookupEntity({variables: {id}});
      setEntity(assertOperationalEntityPresent(refreshed));
    } catch (error) {
      toastError(error as Error);
    } finally {
      setWorking(false);
    }
  };

  const updateField = (field: keyof typeof emptyForm, value: string) => {
    setForm(prev => ({...prev, [field]: value}));
    if (field === "abbreviation" || field === "nameplateDescription") {
      setErrors(prev => ({...prev, [field]: undefined}));
    }
  };

  const handleAdd = async () => {
    const nextErrors: typeof errors = {};
    if (!form.abbreviation.trim()) nextErrors.abbreviation = "OEM code is required";
    if (!form.nameplateDescription.trim()) nextErrors.nameplateDescription = "Nameplate is required";
    if (nextErrors.abbreviation || nextErrors.nameplateDescription) {
      setErrors(nextErrors);
      return;
    }

    const newOem: OemInput = {
      abbreviation: form.abbreviation.trim(),
      id: form.id.trim() || null,
      nameplateDescription: form.nameplateDescription.trim(),
      locationId: form.locationId.trim() || null
    };

    await persistOems([...toOemInputs(oems), newOem]);
    setForm(emptyForm);
    setErrors({});
  };

  const handleDelete = async (target: Oem) => {
    const targetKey = oemUniqueKey(target);
    const next = toOemInputs(oems).filter(oem => oemUniqueKey(oem) !== targetKey);
    await persistOems(next);
  };

  const stopEditing = () => {
    setEditing(false);
    setForm(emptyForm);
    setErrors({});
  };

  return (
    <TabRow
      name={"operational-oems"}
      header={
        <h4 style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          OEMs
          {editing ? (
            <Tooltip position={"top"} toolTipContent={"Done editing"} size={"small"}>
              <Button
                data-testid={"synthetic-oem-done"}
                className={"edit-button edit-button--inline"}
                icon={<XMarkIcon />}
                size={"default"}
                buttonStyle={"secondary"}
                disabled={working}
                onClick={stopEditing}
              />
            </Tooltip>
          ) : (
            <EditButton
              onClick={() => setEditing(true)}
              qualifier={"oems"}
              tooltip={"Edit OEMs"}
              inline={false}
            />
          )}
        </h4>
      }>
      <SimpleTable
        id={`synthetic-oem-table-${id}`}
        data-testid={`synthetic-oem-table-${id}`}
        dataDensity={"small"}
        className={"oem-table"}
        background={"white"}>
        <TableHead>
          <TableRow>
            <TableCell>OEM</TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Nameplate</TableCell>
            <TableCell>Location</TableCell>
            {editing && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {oems.map(oem => (
            <TableRow key={oemUniqueKey(oem)} className={"oem-table-row"}>
              <TableCell>{oem.abbreviation}</TableCell>
              <TableCell>{oem.id}</TableCell>
              <TableCell>{oem.nameplateDescription}</TableCell>
              <TableCell>{oem.locationId}</TableCell>
              {editing && (
                <TableCell className={"oem-action-cell"}>
                  <Button
                    data-testid={`synthetic-oem-delete-${oemUniqueKey(oem)}`}
                    className={"edit-button edit-button--inline"}
                    buttonStyle={"secondary"}
                    size={"small"}
                    icon={<TrashIcon />}
                    disabled={working}
                    onClick={() => handleDelete(oem)}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </SimpleTable>
      {oems.length === 0 && (
        <Typography
          tag={"span"}
          className={"no-oems"}
          variant={"body-sm"}
          data-testid={"synthetic-oem-empty"}>
          No OEMs
        </Typography>
      )}

      {editing && (
        <div className={"synthetic-oem-add-row"} data-testid={"synthetic-oem-add-row"}>
          <div className={"synthetic-oem-field"}>
            <TextInput
              id={"synthetic-oem-abbreviation"}
              data-testid={"synthetic-oem-abbreviation"}
              name={"synthetic-oem-abbreviation"}
              label={"OEM"}
              displayLabel={false}
              size={"small"}
              value={form.abbreviation}
              onChange={e => updateField("abbreviation", e.target.value)}
              disabled={working}
              hasError={!!errors.abbreviation}
              errorMessage={errors.abbreviation}
              placeholder={"e.g. GM"}
              required
            />
          </div>
          <div className={"synthetic-oem-field"}>
            <TextInput
              id={"synthetic-oem-id"}
              data-testid={"synthetic-oem-id"}
              name={"synthetic-oem-id"}
              label={"ID"}
              displayLabel={false}
              size={"small"}
              value={form.id}
              onChange={e => updateField("id", e.target.value)}
              disabled={working}
              placeholder={"Dealer code"}
            />
          </div>
          <div className={"synthetic-oem-field"}>
            <TextInput
              id={"synthetic-oem-nameplate"}
              data-testid={"synthetic-oem-nameplate"}
              name={"synthetic-oem-nameplate"}
              label={"Nameplate"}
              displayLabel={false}
              size={"small"}
              value={form.nameplateDescription}
              onChange={e => updateField("nameplateDescription", e.target.value)}
              disabled={working}
              hasError={!!errors.nameplateDescription}
              errorMessage={errors.nameplateDescription}
              placeholder={"e.g. Chevrolet"}
              required
            />
          </div>
          <div className={"synthetic-oem-field"}>
            <TextInput
              id={"synthetic-oem-location"}
              data-testid={"synthetic-oem-location"}
              name={"synthetic-oem-location"}
              label={"Location"}
              displayLabel={false}
              size={"small"}
              value={form.locationId}
              onChange={e => updateField("locationId", e.target.value)}
              disabled={working}
              placeholder={"GM dealer code"}
            />
          </div>
          <Tooltip position={"top"} toolTipContent={"Add OEM"} size={"small"}>
            <Button
              id={"synthetic-oem-add"}
              data-testid={"synthetic-oem-add"}
              className={"synthetic-oem-add-button"}
              buttonStyle={"primary"}
              size={"small"}
              onClick={handleAdd}
              disabled={working}
              isLoading={working}
              icon={<PlusIcon />}
            />
          </Tooltip>
        </div>
      )}
    </TabRow>
  );
};

const toOemInputs = (oems: Oem[]): OemInput[] =>
  oems.map(({abbreviation, id, nameplateDescription, locationId}) => ({
    abbreviation,
    id,
    nameplateDescription,
    locationId
  }));
